'use client';

import * as React from 'react';

interface TypewriterOptions {
  /** Off means "no animation at all": the target element is emptied. */
  enabled?: boolean;
  /**
   * Per-character delays, both jittered so they read as a person. Erasing runs
   * about three times faster than typing — it's the dead time between phrases.
   */
  typeMs?: number;
  deleteMs?: number;
  /** Pause on the finished phrase, and on the empty field before the next one. */
  holdMs?: number;
  pauseMs?: number;
}

/**
 * Types a list of phrases out one character at a time, erases each, and moves
 * on to the next — the placeholder equivalent of someone searching.
 *
 * Writes straight into `targetRef`'s textContent: a per-character setState would
 * re-render the host (input, icons, popover anchor) several times a second while
 * the page just sits there. React never sees the ticks.
 *
 * Reduced motion gets the first phrase whole, and a hidden tab stops the timers
 * rather than burning ticks nobody sees.
 */
export function useTypewriter(
  targetRef: React.RefObject<HTMLElement | null>,
  phrases: string[],
  options: TypewriterOptions = {},
): void {
  const { enabled = true, typeMs = 55, deleteMs = 18, holdMs = 1600, pauseMs = 400 } = options;

  const reducedMotion = usePrefersReducedMotion();
  const pageVisible = usePageVisible();

  // Callers build the list inline (t.raw(), .map()), so its identity changes
  // every render — the content is what should restart the animation.
  const key = phrases.join('\u0000');
  const phrasesRef = React.useRef(phrases);
  phrasesRef.current = phrases;

  // `key` stands in for the phrases list (see phrasesRef above).
  // biome-ignore lint/correctness/useExhaustiveDependencies: key tracks phrases content
  React.useEffect(() => {
    const el = targetRef.current;
    if (!el) return;
    const list = phrasesRef.current;
    if (!enabled || list.length === 0) {
      el.textContent = '';
      return;
    }
    if (reducedMotion) {
      el.textContent = list[0] ?? '';
      return;
    }
    if (!pageVisible) return;

    let state = { phrase: 0, chars: 0, deleting: false };
    let timer: number;

    const step = () => {
      const current = list[state.phrase % list.length] ?? '';
      el.textContent = current.slice(0, state.chars);

      let delay: number;
      if (!state.deleting) {
        delay = state.chars < current.length ? jitter(typeMs) : holdMs;
      } else {
        delay = state.chars > 0 ? jitter(deleteMs) : pauseMs;
      }

      timer = window.setTimeout(() => {
        if (!state.deleting) {
          if (state.chars < current.length) state = { ...state, chars: state.chars + 1 };
          else state = { ...state, deleting: true };
        } else if (state.chars > 0) {
          state = { ...state, chars: state.chars - 1 };
        } else {
          state = { phrase: (state.phrase + 1) % list.length, chars: 0, deleting: false };
        }
        step();
      }, delay);
    };

    step();
    return () => clearTimeout(timer);
  }, [enabled, key, reducedMotion, pageVisible, typeMs, deleteMs, holdMs, pauseMs, targetRef]);
}

/** ±25%, so evenly-timed keystrokes don't read as a machine. */
function jitter(ms: number) {
  return ms * (0.75 + Math.random() * 0.5);
}

function usePrefersReducedMotion() {
  // Server and first paint assume motion is fine; the effect corrects it before
  // the first character would have landed anyway.
  const [reduced, setReduced] = React.useState(false);

  React.useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(query.matches);
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

function usePageVisible() {
  const [visible, setVisible] = React.useState(true);

  React.useEffect(() => {
    const onChange = () => setVisible(!document.hidden);
    onChange();
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);

  return visible;
}

export type { TypewriterOptions };
